import { ChangeEvent, DragEvent, MouseEvent, useState } from 'react';
import { FieldValues, useController } from 'react-hook-form';
import styled, { css } from 'styled-components';

import { ReactComponent as VideoPlaceholder } from '@/assets/icons/video-placeholder-large.svg';
import { ReactComponent as VideoIcon } from '@/assets/icons/video-placeholder.svg';
import { Label, Larger } from '@/components/Forms';
import { SrOnly } from '@/components/Layout/Parts';
import ProgressBar from '@/components/ProgressBar';
import Text from '@/components/Text';
import useAlerts from '@/hooks/useAlerts';
import { humanFileSize, isObject } from '@/utils/helpers';
import { uploadFileToS3 } from '@/utils/s3';

import { DefaultProps, VideoFieldProps } from '../FormField';

export const Dropzone = styled.div<{ $dragging: boolean }>`
  border-radius: 4px;
  border: 1px dashed ${({ theme }) => theme.colors.lightGray};
  color: ${({ theme }) => theme.colors.gray};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px;
  ${({ theme, $dragging }) =>
    $dragging &&
    css`
      border-color: ${theme.colors.purpleLight};
    `}
`;

const Title = styled(Text)`
  margin: 10px 0 6px;
`;

const Notice = styled(Text)`
  opacity: 0.5;
  text-align: center;
`;

const Preview = styled.div`
  border-radius: 4px;
  border: 1px solid ${({ theme }) => theme.colors.grayscale};
  display: flex;
  align-items: center;
  padding: 16px;
  position: relative;
`;

const FileInfo = styled.div`
  overflow: hidden;
  flex: 1;
  text-align: left;
  padding-left: 16px;
`;

const FileName = styled(Text)`
  margin-bottom: 8px;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ClearButton = styled.button`
  position: absolute;
  top: 10px;
  right: 10px;
  transition: 0.2s ease-out opacity;
  &:hover {
    opacity: 0.75;
  }
`;

const InnerLabel = styled(Text)`
  cursor: pointer;
`;

const StyledProgressBar = styled(ProgressBar)`
  height: 8px;
`;

const MultipleProgressWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ALLOWED_VIDEO_TYPES = ['video/mp4'];

type Props<T extends FieldValues> = Omit<
  DefaultProps<T> & VideoFieldProps,
  'type'
>;

const VideoFieldV2 = <T extends FieldValues>({
  name,
  label,
  control,
  fileUploadedCallback,
  notes = (
    <>
      Format: MP4
      <br />
      Max file size: 5GB
    </>
  ),
  existingMedia,
  accept = 'video/mp4',
}: Props<T>) => {
  const [dragging, setDragging] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [media, setMedia] = useState(existingMedia ?? null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProggress, setUploadProgress] = useState<
    | number
    | {
        [key: number]: number;
      }
  >(0);

  const {
    field: { onChange },
  } = useController({
    name,
    control,
  });

  const alerts = useAlerts();

  const onFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    setFile(e.target.files?.[0] ?? null);

    if (e.target.files?.[0]) {
      onVideoUpload(e.target.files[0]);
    }
  };

  const handleDragOver = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(true);
  };

  const handleDragEnter = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (e.dataTransfer?.items?.length) {
      setDragging(true);
    }
  };

  const handleDragLeave = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
  };

  const handleDrop = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);

    if (e.dataTransfer?.files?.length) {
      const droppedFile = e.dataTransfer.files[0];

      //   VALIDATION
      if (!ALLOWED_VIDEO_TYPES.includes(droppedFile.type)) {
        return alerts.error(
          'Invalid file type!',
          `Please use video of type ${ALLOWED_VIDEO_TYPES.join(', ')}`,
        );
      }

      setFile(droppedFile);
      onVideoUpload(droppedFile);
      e.dataTransfer?.clearData();
    }
  };

  const onVideoUpload = async (file: File) => {
    try {
      setUploadProgress(0);
      setIsUploading(true);

      const filename = await uploadFileToS3(
        file,
        (progressEvent: ProgressEvent) => {
          setUploadProgress(
            Math.round((progressEvent.loaded / progressEvent.total) * 100),
          );
        },
      );

      if (!filename) {
        alerts.error('Failed to upload video', 'Please try again');
        setFile(null);
        return setIsUploading(false);
      }

      onChange(filename);
      fileUploadedCallback?.(file);
      setIsUploading(false);
    } catch (err) {
      alerts.error('Failed to upload video', 'Please try again');
      setFile(null);
      setIsUploading(false);
    }
  };

  const onClear = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setFile(null);
    setMedia(null);
    onChange('');
  };

  const renderUploadProgress = () => {
    if (!isObject(uploadProggress)) {
      return (
        <StyledProgressBar
          progress={uploadProggress as number}
          color="primary"
          showPercentage
        />
      );
    }
    return (
      <MultipleProgressWrap>
        {Object.keys(uploadProggress).map((key) => (
          <div key={key}>
            <StyledProgressBar
              progress={uploadProggress[key as keyof typeof uploadProggress]}
              color="primary"
              showPercentage
            />
          </div>
        ))}
      </MultipleProgressWrap>
    );
  };

  return (
    <Larger>
      {!!label && <Label>{label}</Label>}
      {isUploading ? (
        renderUploadProgress()
      ) : (
        <>
          {media || file ? (
            <Preview>
              <ClearButton onClick={onClear}>
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M12 4L4 12"
                    stroke="#222222"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M12 12L4 4"
                    stroke="#222222"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </ClearButton>
              <VideoPlaceholder />
              <FileInfo>
                <FileName as="div" $type="smallMedium">
                  {media?.name ?? file?.name}
                </FileName>
                <Text as="div" $type="smallMedium" $color="gray">
                  {humanFileSize((media?.size ?? file?.size)!, true)}
                </Text>
              </FileInfo>
            </Preview>
          ) : (
            <Dropzone
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              $dragging={dragging}
            >
              <VideoIcon />
              <Title $type="smallBold" $color="gray" as="div">
                Drop video here or{' '}
                <InnerLabel
                  as="label"
                  $type="smallBold"
                  $color="purple"
                  htmlFor={`${name}-input`}
                >
                  browse
                  <SrOnly
                    as="input"
                    type="file"
                    accept={accept}
                    id={`${name}-input`}
                    onChange={onFileChange}
                  />
                </InnerLabel>
              </Title>
              <Notice $type="extraSmall" $color="gray" as="div">
                {notes}
              </Notice>
            </Dropzone>
          )}
        </>
      )}
    </Larger>
  );
};

export default VideoFieldV2;
